import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Dialog, DialogActions, DialogContent, DialogTitle, Tooltip } from '@mui/material'
import { Asterisk, Info, X } from 'lucide-react'
import Button from '../ui/Button'

export type CompositeOptionValue = {
  pattern: string
  valuePosition: number
  valuePositionEnd: number
}

type CompositeOptionDialogProps = {
  open: boolean
  digits: number
  subgroupName: string
  start: number
  end: number
  value?: string
  onClose: () => void
  onApply: (value: CompositeOptionValue) => void
}

const CONCRETE_VALUE_PATTERN = /^[A-Z0-9#]$/

export const CompositeOptionDialog: React.FC<CompositeOptionDialogProps> = ({
  open,
  digits,
  subgroupName,
  start,
  end,
  value = '',
  onClose,
  onApply,
}) => {
  const initialCharacters = useMemo(() => (
    Array.from({ length: digits }, (_, position) => {
      const character = String(value || '').trim().toUpperCase()[position] || ''
      return position >= start && position <= end && character !== '*' ? character : ''
    })
  ), [digits, end, start, value])
  const [characters, setCharacters] = useState<string[]>(initialCharacters)
  const inputRefs = useRef<Array<HTMLInputElement | null>>([])

  useEffect(() => {
    if (!open) return
    setCharacters(initialCharacters)
    const firstConcretePosition = initialCharacters.findIndex(Boolean)
    const initialFocusPosition = firstConcretePosition >= start && firstConcretePosition <= end
      ? firstConcretePosition
      : start
    window.setTimeout(() => inputRefs.current[initialFocusPosition]?.focus(), 0)
  }, [end, initialCharacters, open, start])

  const concretePositions = characters.reduce<number[]>((positions, character, position) => {
    if (position >= start && position <= end && character) positions.push(position)
    return positions
  }, [])
  const valuePosition = concretePositions[0] ?? null
  const valuePositionEnd = concretePositions[concretePositions.length - 1] ?? null
  const contiguous = concretePositions.every((position, index) => index === 0 || position === concretePositions[index - 1] + 1)
  const pattern = Array.from({ length: digits }, (_, position) => characters[position] || '*').join('')
  const complete = valuePosition !== null
    && valuePositionEnd !== null
    && contiguous

  const updateCharacter = (index: number, rawValue: string) => {
    const nextCharacter = rawValue.slice(-1).toUpperCase()
    if (nextCharacter && !CONCRETE_VALUE_PATTERN.test(nextCharacter)) return
    setCharacters(current => current.map((character, position) => (
      position === index ? nextCharacter : character
    )))
    if (nextCharacter) inputRefs.current[index + 1]?.focus()
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
        <div>
          <div className="text-base font-semibold text-gray-900">Configure option</div>
          <div className="text-xs font-normal text-gray-500">{subgroupName}</div>
        </div>
        <button type="button" onClick={onClose} className="rounded p-1 text-gray-500 hover:bg-gray-100" aria-label="Close option dialog">
          <X className="h-4 w-4" />
        </button>
      </DialogTitle>
      <DialogContent dividers>
        <div className="mb-4 flex items-start gap-2 rounded-md border border-blue-100 bg-blue-50 px-3 py-2 text-xs text-[#1D5176]">
          <Info className="mt-0.5 h-4 w-4 shrink-0" />
          <span>Enter values in the positions used by this option. Leave a position blank to store Any (*).</span>
        </div>

        <div className="flex flex-wrap justify-center gap-3 py-3">
          {Array.from({ length: digits }, (_, position) => {
            const inSubgroup = position >= start && position <= end
            return (
              <div key={position} className="flex flex-col items-center gap-2">
                <span className="text-[11px] font-medium text-gray-500">Pos {position + 1}</span>
                {inSubgroup ? (
                  <input
                    ref={element => { inputRefs.current[position] = element }}
                    value={characters[position] || ''}
                    onChange={event => updateCharacter(position, event.target.value)}
                    onKeyDown={event => {
                      if (event.key === 'Backspace' && !characters[position]) inputRefs.current[position - 1]?.focus()
                    }}
                    maxLength={1}
                    placeholder="*"
                    aria-label={`${subgroupName} position ${position + 1}`}
                    className="h-14 w-14 rounded-lg border-2 border-[#1D74D8] bg-blue-50 text-center text-2xl font-semibold uppercase text-[#1D5176] outline-none focus:ring-2 focus:ring-blue-200"
                  />
                ) : (
                  <Tooltip title="Any">
                    <div className="flex h-14 w-14 items-center justify-center rounded-lg border border-gray-300 bg-gray-50 text-gray-500">
                      <Asterisk className="h-7 w-7" />
                    </div>
                  </Tooltip>
                )}
                <span className="h-4 text-[11px] text-gray-500">{characters[position] ? 'Value' : 'Any'}</span>
              </div>
            )
          })}
        </div>

        <div className="mt-3 rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm">
          <span className="text-gray-500">Stored pattern: </span>
          <span className="font-mono font-semibold text-gray-800">{pattern || '—'}</span>
        </div>
        {!contiguous && concretePositions.length > 1 && (
          <div className="mt-2 text-xs text-[#B3261E]" role="alert">Selected value positions must be contiguous.</div>
        )}
        {concretePositions.length === 0 && (
          <div className="mt-2 text-xs text-gray-500">Select at least one position and enter its value.</div>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button variant="secondary" onClick={onClose}>Cancel</Button>
        <Button
          disabled={!complete}
          onClick={() => complete && onApply({
            pattern,
            valuePosition: valuePosition as number,
            valuePositionEnd: valuePositionEnd as number,
          })}
        >Apply</Button>
      </DialogActions>
    </Dialog>
  )
}

export default CompositeOptionDialog
